import React, { useContext, useState, useEffect } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Chip, Typography, IconButton, Badge, Paper } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import { GlobalContext } from "../../contexts/GlobalContext"
import getIconComponent from "../utils/getIconComponent"
import { PlayCircleOutline, CloudOff, CloudDone, Sync } from "@mui/icons-material"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"
import localforage from "localforage"
import Swal from "sweetalert2"
import { api } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const getTipoEncuestaLabel = (tipo) => {
  switch (tipo) {
    case "RAP":
      return "Rápida"
    case "SAT":
      return "Satisfacción"
    case "PON":
      return "Ponderación"
    default:
      return tipo
  }
}

const EjecEncuestasIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [encuestasEnCola, setEncuestasEnCola] = useState([])
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarColaLocal()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const cargarColaLocal = async () => {
    const cola = (await localforage.getItem("encuestas_offline_queue")) || []
    setEncuestasEnCola(cola)
  }

  const sincronizarEncuestas = async () => {
    if (isOffline) return Swal.fire("Aún sin conexión", "Busque señal para emitir las encuestas.", "warning")
    if (encuestasEnCola.length === 0)
      return Swal.fire("Todo al día", "No hay encuestas pendientes de envío.", "success")

    Swal.fire({ title: "Sincronizando Encuestas...", allowOutsideClick: false, didOpen: () => Swal.showLoading() })
    setIsSyncing(true)

    let colaRestante = [...encuestasEnCola]
    let errores = 0

    for (let i = 0; i < encuestasEnCola.length; i++) {
      try {
        // Post directo sin Celery
        const res = await api.post("/EjecEncuestas/saveRespuestaEncuesta", encuestasEnCola[i])
        const data = res?.data !== undefined ? res.data : res

        if (data.success) {
          colaRestante = colaRestante.filter((_, index) => index !== i)
        } else {
          errores++
        }
      } catch (err) {
        errores++
        console.error("Fallo al subir encuesta", err)
      }
    }

    await localforage.setItem("encuestas_offline_queue", colaRestante)
    setEncuestasEnCola(colaRestante)
    setIsSyncing(false)

    if (colaRestante.length === 0) {
      Swal.fire("¡Sincronización Completa!", "Todas las encuestas fueron transmitidas.", "success")
    } else {
      Swal.fire(
        "Sincronización Parcial",
        `Algunas encuestas (${errores}) fallaron por problemas de red. Intente de nuevo.`,
        "warning",
      )
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Panel Superior: Estado de Red y Sincronización Rápida */}
        <Paper
          elevation={1}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, p: 2, borderRadius: 2 }}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? (
              <CloudOff color="error" sx={{ fontSize: 32, mr: 1 }} />
            ) : (
              <CloudDone color="success" sx={{ fontSize: 32, mr: 1 }} />
            )}
            <Typography variant="subtitle1" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "OFFLINE (SIN SEÑAL)" : "CONECTADO"}
            </Typography>
          </Box>
          <Badge badgeContent={encuestasEnCola.length} color="error">
            <IconButton
              onClick={sincronizarEncuestas}
              disabled={encuestasEnCola.length === 0 || isOffline || isSyncing}
              sx={{ bgcolor: "#e3f2fd" }}
            >
              <Sync color={encuestasEnCola.length > 0 ? "primary" : "disabled"} />
            </IconButton>
          </Badge>
        </Paper>

        <Box
          sx={{
            textAlign: "center",
            mb: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#196C87",
          }}
        >
          <PlayCircleOutline sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Ejecución de Encuestas Disponibles
          </Typography>
        </Box>

        <Box className={StyledRoot}>
          <CustomConditionalActionsTableServer
            endpoint="/EjecEncuestas/getEncuestasDisponibles"
            errorMsgFilterSearch="Error al cargar la bandeja de encuestas para ejecución"
            queryKeyModal="EjecEncuestas_Bandeja_Principal"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) =>
                  action?.acccaption === "EJECUTAR" ||
                  action?.acccaption === "RESPONDER" ||
                  action?.acccaption === "GESTIONAR",
              )
              const actions = []
              if (ejecutarAction) {
                actions.push({
                  label: "Iniciar Encuesta",
                  key: "ejecutar",
                  icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
                  onClick: (row) => navigate("ejecutar", { state: { encuesta: row.original } }),
                })
              }
              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )
              const toolbarActions = []
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      onClick: ({ columns, data }) => {
                        const title = "Encuestas Disponibles"
                        device === "sm"
                          ? handleExportDataPdfSMScreen(columns, data, title, title)
                          : handleExportDataPdfLGScreen(columns, table.getCoreRowModel().rows, title, title)
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      onClick: ({ data }) => handleAllExportDataCSV(data, "Encuestas_Disponibles_SIAC"),
                    },
                  ],
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "enccodigo",
                header: "Código",
                size: 130,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "enctitulo",
                header: "Título de la Encuesta",
                size: 300,
                Cell: ({ cell }) => <span style={{ fontWeight: "500" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "enctipo",
                header: "Tipo",
                size: 130,
                Cell: ({ cell }) => (
                  <Chip
                    label={getTipoEncuestaLabel(cell.getValue())}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: "bold" }}
                  />
                ),
              },
              {
                accessorKey: "encpublica",
                header: "Modalidad",
                size: 140,
                Cell: ({ cell }) => {
                  const esPublica = cell.getValue() === 1
                  return (
                    <Chip
                      label={esPublica ? "PÚBLICA" : "ASIGNADA"}
                      color={esPublica ? "success" : "info"}
                      size="small"
                      variant={esPublica ? "filled" : "outlined"}
                    />
                  )
                },
              },
              {
                accessorKey: "encdescri",
                header: "Descripción",
                size: 350,
                Cell: ({ cell }) => {
                  const desc = cell.getValue()
                  return desc ? (
                    <span style={{ fontSize: "0.85rem", color: "#555" }}>{desc}</span>
                  ) : (
                    <i>Sin descripción</i>
                  )
                },
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EjecEncuestasIndex
